import React, { useState, useEffect, useMemo } from "react";
import SlidePanel from "../add_customer/SlidePanel";
import { X } from "lucide-react";

const FeePayment = ({
  id_R,
  closeFeePayment,
  isFeePaymentOpen,
  mpesaNumber,
  setMpesaNumber,
  customer,
  handleRegisterShares,
  joiningFee,
  setJoiningFee,
  savingsAmount,
  setSavingsAmount,
  sharesAmount,
  setSharesAmount,
}) => {
  const [formattedJoiningFee, setFormattedJoiningFee] = useState(1000);
  const [formattedSavingsAmount, setFormattedSavingsAmount] = useState("");
  const [formattedSharesAmount, setFormattedSharesAmount] = useState("");

  const handleJoiningFeeChange = (e) => {
    let value = e.target.value;

    const rawValue = value.replace(/[^0-9]/g, "");
    const formattedValue =
      rawValue === "" ? "" : new Intl.NumberFormat()?.format(rawValue);

    setJoiningFee(rawValue);
    setFormattedJoiningFee(formattedValue);
  };

  const handleSavingsChange = (e) => {
    let value = e.target.value;

    const rawValue = value.replace(/[^0-9]/g, "");
    const formattedValue =
      rawValue === "" ? "" : new Intl.NumberFormat()?.format(rawValue);

    setSavingsAmount(rawValue);
    setFormattedSavingsAmount(formattedValue);
  };

  const handleSharesChange = (e) => {
    let value = e.target.value;

    const rawValue = value.replace(/[^0-9]/g, "");
    const formattedValue =
      rawValue === "" ? "" : new Intl.NumberFormat()?.format(rawValue);

    setSharesAmount(rawValue);
    setFormattedSharesAmount(formattedValue);
  };

  useEffect(() => {
    if (customer) {
      setMpesaNumber(customer?.mobileno || "");
    }
  }, [customer]);

  const handleMpesaNumberChange = (e) => {
    setMpesaNumber(e.target.value);
  };

  // ✅ Total calculation
  const totalAmount = useMemo(() => {
    return (
      Number(joiningFee) +
      Number(savingsAmount || 0) +
      Number(sharesAmount || 0)
    );
  }, [joiningFee, savingsAmount, sharesAmount]);

  // ✅ Phone validation (UNCHANGED)
  const isValidPhone = useMemo(() => {
    if (!mpesaNumber) return false;

    const cleaned = mpesaNumber.replace(/\s+/g, "");

    const validPatterns = [/^\+254\d{9}$/, /^254\d{9}$/, /^0\d{9}$/];

    return validPatterns?.some((pattern) => pattern?.test(cleaned));
  }, [mpesaNumber]);

  const isValidAmounts = useMemo(() => {
    return Number(joiningFee) > 0;
  }, [joiningFee]);

  const isFormValid = isValidPhone && isValidAmounts;

  return (
    <SlidePanel isOpen={isFeePaymentOpen} onClose={closeFeePayment}>
      <div className="relative flex flex-col h-full bg-white rounded-[12px]">
        {/* Close Button (same as NextOfKin) */}
        <div
          onClick={closeFeePayment}
          className="absolute top-0 right-0 cursor-pointer h-[40px] w-[40px] bg-[#FF0000] rounded-tr-[10px] rounded-bl-[20px] flex items-center justify-center z-10"
        >
          <X color="#ffffff" />
        </div>

        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="mt-12 px-5">
            <p className="text-[#000] text-xl font-bold">
              Registration Fee Payment
            </p>
            <div className="text-[#000] text-[15px] font-normal">
              <p>
                Enter the M-Pesa phone number that will be prompted to pay the
                registration fee. By default, the member’s primary number is
                prefilled.
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 pb-2 mt-3 flex flex-col gap-3">
            <div>
              <label className="text-md text-[#000] mb-1 block">
                Joining Fee
              </label>

              <div className="flex h-11 w-full rounded-md overflow-hidden border border-[#171717]">
                {/* Currency */}
                <div className="flex items-center px-5 bg-[#EAEAEA] text-[14px] text-[#4A4747]">
                  KES
                </div>
                {/* Input */}
                <input
                  type="text"
                  value={formattedJoiningFee}
                  onChange={handleJoiningFeeChange}
                  placeholder="Enter amount"
                  className="flex-1 px-3 bg-transparent outline-none text-[#060606] text-[14px]"
                />
              </div>
            </div>

            {/* Savings (Optional) */}
            <div className="bg-[#F5F5F5] p-4 rounded">
              <p className="font-semibold text-sm mb-2">
                Option: Add Saving & Shares Amount
              </p>

              <label className="text-md text-[#000]">Savings Amount</label>
              <div className="flex h-11 w-full rounded-md overflow-hidden border border-[#171717] mb-2">
                <div className="flex items-center px-5 bg-[#EAEAEA] text-[14px] text-[#4A4747]">
                  KES
                </div>

                <input
                  type="text"
                  value={formattedSavingsAmount}
                  onChange={handleSavingsChange}
                  placeholder="Enter amount"
                  className="flex-1 px-2 bg-[#F5F5F5] outline-none text-[14px] text-[#060606]"
                />
              </div>

              <label className="text-md text-[#000]">Shares Amount</label>
              <div className="flex h-11 w-full rounded-md overflow-hidden border border-[#171717]">
                <div className="flex items-center px-5 bg-[#EAEAEA] text-[14px] text-[#4A4747]">
                  KES
                </div>

                <input
                  type="text"
                  value={formattedSharesAmount}
                  onChange={handleSharesChange}
                  placeholder="Enter amount"
                  className="flex-1 px-2 bg-[#F5F5F5] outline-none text-[14px] text-[#060606]"
                />
              </div>
            </div>

            {/* Total */}
            <div>
              <div className="text-[16px] text-[#000000]">Total Amount</div>
              <div className="text-[15px] text-[#4A4747]">
                Total amount the member will be prompted to pay
              </div>
              <div className="bg-[#F0FFFE] h-11 flex items-center justify-center rounded text-[14px]">
                <div className="text-[#4A4747] font-normal  px-2">KES</div>
                <div className="text-[#060606] font-normal">
                  {Number(totalAmount || "0.00").toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-5 mt-2">
              {/* Phone Input (styled like NextOfKin) */}
              <div className="flex flex-col">
                <label className="mb-1 text-md text-[#000]">
                  M-PESA Phone Number
                </label>
                <input
                  type="text"
                  value={mpesaNumber}
                  onChange={handleMpesaNumberChange}
                  placeholder="e.g. 07XXXXXXXX or 2547XXXXXXXX"
                  className="w-full h-11 px-4 rounded bg-transparent text-[#000000] border-[1px] border-[#000000]"
                />
              </div>
            </div>
          </div>

          {/* Footer (fixed like NextOfKin) */}
          <div className="p-4">
            <button
              onClick={handleRegisterShares}
              disabled={!isValidPhone}
              className={`w-full text-white px-6 py-2 rounded flex items-center justify-center
                ${
                  isValidPhone
                    ? "bg-[#005C53]"
                    : "bg-[#D9D9D9] cursor-not-allowed"
                }
              `}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
};

export default FeePayment;
